import { clearCachedTenantToken } from '@/helpers/supportChatWidgetToken'
import type { CustomerType } from '@/types'

export type StoreStateInterface = {
  authenticated: boolean
  profile: CustomerType
  loading: boolean
  authRequestFinished: boolean
  authMethod: string
}

export const useAuthStore = defineStore('auth', () => {
  const authenticated = ref<boolean>(false)
  const authRequestFinished = ref<boolean>(false)
  const profile = ref<CustomerType | null>(null)
  const loading = ref<boolean>(false)
  const authMethod = ref<string>(import.meta.env.VITE_AUTH_METHOD || 'SSO')

  const inAppNotificationPreferences = computed(
    () =>
      profile.value?.owner?.preference?.notification_preferences
        ?.inapp_notifications,
  )

  const getLoadingState = computed(() => loading.value)
  const isInspectionService = computed(
    () => profile.value?.owner?.workflow === 'inspection_service',
  )

  function updateProfile(data: CustomerType | null) {
    profile.value = data
  }

  function authenticate(auth: boolean) {
    authenticated.value = auth
  }

  function setAuthRequestFinished(authRequestFinishedState: boolean) {
    authRequestFinished.value = authRequestFinishedState
  }

  function toggleLoader(isLoading: boolean) {
    loading.value = isLoading
  }

  async function login({ user, token }: { user: CustomerType; token: string }) {
    authenticated.value = true
    authRequestFinished.value = true

    localStorage.setItem('auth', token)

    if (window.posthog) {
      window.posthog.identify?.(user.email, {
        email: user.email,
        insurer: user.owner?.name,
      })
    }

    return true
  }

  function logout() {
    clearCachedTenantToken()
    updateProfile(null)
    localStorage.removeItem('auth')
    authenticate(false)
  }

  return {
    authenticated,
    authRequestFinished,
    profile,
    isInspectionService,
    loading,
    getLoadingState,
    updateProfile,
    authenticate,
    setAuthRequestFinished,
    toggleLoader,
    login,
    logout,
    inAppNotificationPreferences,
    authMethod,
  }
})
